<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Menu Component
      p.text--secondary A versatile dropdown menu component with customizable content and positioning.

    // Menu Examples
    .component-section.mb-8#basic-menu
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-menu')") Basic Menu Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in menuVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Menu Implementation
            yz-menu(v-bind="variation.props")
              template(v-slot:activator="{ on, attrs }")
                yz-btn(v-bind="{ ...attrs, ...variation.buttonProps }", v-on="on")
                  template(v-if="variation.buttonProps.icon")
                    v-icon {{ variation.buttonProps.icon }}
                  template(v-else) {{ variation.buttonProps.text || variation.buttonProps.label }}
              yz-list
                template(v-for="(item, index) in variation.items")
                  yz-list-item(v-if="item.type !== 'divider'" :key="index" @click="handleMenuClick(item.action, item.label)")
                    div(:class="item.class") {{ item.label }}
                  yz-divider(v-else :key="index" color="yz-neutral-300")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in menuProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
      
      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in menuSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      h4.mb-3 Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in menuEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null
    }
  },
  computed: {
    menuVariations() {
      return [
        {
          name: 'Basic Menu',
          id: 'basic-menu-example',
          description: 'Simple dropdown menu with text button activator',
          label: 'Basic Menu',
          hint: 'Click to open menu',
          props: {
            offsetY: true,
          },
          buttonProps: {
            label: 'Open Menu',
            size: 'md'
          },
          items: [
            { label: 'Edit', action: 'edit' },
            { label: 'Duplicate', action: 'duplicate' },
            { type: 'divider' },
            { label: 'Delete', action: 'delete', class: 'danger--text' }
          ],
          code: `yz-menu(offset-y)
  template(v-slot:activator="{ on, attrs }")
    yz-btn(v-bind="attrs" v-on="on" label="Open Menu" size="md")
  yz-list
    yz-list-item(@click="editItem")
      div Edit
    yz-list-item(@click="duplicateItem")
      div Duplicate
    yz-divider
    yz-list-item(@click="deleteItem")
      .danger--text Delete`
        },
        {
          name: 'Icon Button Menu',
          id: 'icon-menu-example',
          description: 'Menu triggered by an icon button',
          label: 'Icon Menu',
          hint: 'Three dots menu',
          props: {
            offsetY: true,
          },
          buttonProps: {
            iconOnly: true,
            icon: 'mdi-dots-vertical',
            size: 'md',
          },
          items: [
            { label: 'Make Cover Photo', action: 'makeCover' },
            { type: 'divider' },
            { label: 'Delete Photo', action: 'deletePhoto', class: 'danger--text' }
          ],
          code: `yz-menu(offset-y)
  template(v-slot:activator="{ on, attrs }")
    yz-btn(v-bind="attrs" v-on="on" icon size="md")
      v-icon mdi-dots-vertical
  yz-list
    yz-list-item(@click="makeCoverPhoto")
      div Make Cover Photo
    yz-divider
    yz-list-item(@click="deletePhoto")
      .danger--text Delete Photo`
        },
        {
          name: 'Right Aligned Menu',
          id: 'right-menu-example',
          description: 'Menu aligned to the right of the activator',
          label: 'Right Aligned',
          hint: 'Opens to the right',
          props: {
            offsetX: true,
            right: true,
          },
          buttonProps: {
            label: 'Right Menu',
            size: 'md'
          },
          items: [
            { label: 'Profile Settings', action: 'profile' },
            { label: 'Account Settings', action: 'account' },
            { type: 'divider' },
            { label: 'Logout', action: 'logout', class: 'danger--text' }
          ],
          code: `yz-menu(offset-x right)
  template(v-slot:activator="{ on, attrs }")
    yz-btn(v-bind="attrs" v-on="on" label="Right Menu" size="md")
  yz-list
    yz-list-item(@click="goToProfile")
      div Profile Settings
    yz-list-item(@click="goToAccount")
      div Account Settings
    yz-divider
    yz-list-item(@click="logout")
      .danger--text Logout`
        },
        {
          name: 'Top Menu',
          id: 'top-menu-example',
          description: 'Menu that opens above the activator',
          label: 'Top Menu',
          hint: 'Opens above button',
          props: {
            top: true,
            offsetY: true,
          },
          buttonProps: {
            label: 'Top Menu',
            size: 'md'
          },
          items: [
            { label: 'Option 1', action: 'option1' },
            { label: 'Option 2', action: 'option2' },
            { label: 'Option 3', action: 'option3' }
          ],
          code: `yz-menu(top offset-y)
  template(v-slot:activator="{ on, attrs }")
    yz-btn(v-bind="attrs" v-on="on" label="Top Menu" size="md")
  yz-list
    yz-list-item(@click="selectOption1")
      div Option 1
    yz-list-item(@click="selectOption2")
      div Option 2
    yz-list-item(@click="selectOption3")
      div Option 3`
        },
        {
          name: 'Disabled Menu',
          id: 'disabled-menu-example',
          description: 'Menu that is disabled and cannot be opened',
          label: 'Disabled Menu',
          hint: 'Cannot be clicked',
          props: {
            offsetY: true,
            disabled: true
          },
          buttonProps: {
            label: 'Disabled Menu',
            size: 'md',
            disabled: true
          },
          items: [
            { label: 'Option 1', action: 'option1' },
            { label: 'Option 2', action: 'option2' },
            { label: 'Option 3', action: 'option3' }
          ],
          code: `yz-menu(offset-y disabled)
  template(v-slot:activator="{ on, attrs }")
    yz-btn(v-bind="attrs" v-on="on" label="Disabled Menu" size="md" disabled)
  yz-list
    yz-list-item(@click="selectOption1")
      div Option 1
    yz-list-item(@click="selectOption2")
      div Option 2
    yz-list-item(@click="selectOption3")
      div Option 3`
        }
      ]
    },
    menuProps() {
      return [
        {
          name: 'attach',
          type: 'Boolean | String',
          default: 'false',
          description: 'Specifies which DOM element the menu should detach to. String can be any valid querySelector and Object can be any valid Node'
        },
        {
          name: 'left',
          type: 'Boolean',
          default: 'false',
          description: 'Aligns the menu to the left side of the activator'
        },
        {
          name: 'right',
          type: 'Boolean',
          default: 'false',
          description: 'Aligns the menu to the right side of the activator'
        },
        {
          name: 'top',
          type: 'Boolean',
          default: 'false',
          description: 'Aligns the menu to the top of the activator'
        },
        {
          name: 'bottom',
          type: 'Boolean',
          default: 'false',
          description: 'Aligns the menu to the bottom of the activator'
        },
        {
          name: 'offsetX',
          type: 'Boolean',
          default: 'false',
          description: 'Adds horizontal offset to the menu position'
        },
        {
          name: 'offsetY',
          type: 'Boolean',
          default: 'false',
          description: 'Adds vertical offset to the menu position'
        },
        {
          name: 'closeOnClick',
          type: 'Boolean',
          default: 'true',
          description: 'Closes the menu when a list item is clicked'
        },
        {
          name: 'closeOnContentClick',
          type: 'Boolean',
          default: 'true',
          description: 'Closes the menu when clicking on its content'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the menu'
        },
        {
          name: 'eager',
          type: 'Boolean',
          default: 'false',
          description: 'Will force the components content to render on mounted'
        },
        {
          name: 'maxHeight',
          type: 'Number | String',
          default: 'auto',
          description: 'Sets the maximum height for the menu'
        },
        {
          name: 'maxWidth',
          type: 'Number | String',
          default: 'auto',
          description: 'Sets the maximum width for the menu'
        },
        {
          name: 'minWidth',
          type: 'Number | String',
          default: '0',
          description: 'Sets the minimum width for the menu'
        }
      ]
    },
    menuSlots() {
      return [
        {
          name: 'activator',
          description: 'The element that triggers the menu. Provides { on, attrs } scope for event handling'
        },
        {
          name: 'default',
          description: 'The content of the menu'
        }
      ]
    },
    menuEvents() {
      return [
        {
          name: '@input',
          parameters: 'value',
          description: 'Emitted when the menu visibility changes'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    handleMenuClick(action, label) {
      console.log(`Menu action: ${action}, Label: ${label}`)
      // You can implement specific actions here
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>